import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { Helmet } from 'react-helmet-async';

function PublicProfile({ username: propUsername }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("played");
  const isMobile = window.innerWidth <= 768;

  // Get username from URL param if not passed as prop
  let username = propUsername;
  if (!username) {
    const match = window.location.pathname.match(/\/user\/([^/]+)/);
    if (match) username = decodeURIComponent(match[1]);
  }

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    setNotFound(false);
    // Two-step lookup: get UID from /usernames/{username}, then fetch /users/{uid}
    const fetchProfile = async () => {
      try {
        // Normalize username to lowercase for database lookup
        const normalizedUsername = username.toLowerCase();
        const usernameRef = doc(db, "usernames", normalizedUsername);
        const usernameSnap = await getDoc(usernameRef);
        if (!usernameSnap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const { uid } = usernameSnap.data();
        if (!uid) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const userRef = doc(db, "users", uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          // Include the uid in the profile data so we can use it for the map links
          setProfile({ ...userData, uid });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        setNotFound(true);
      }
      setLoading(false);
    };
    fetchProfile();
  }, [username]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/courses.json`)
      .then(res => res.json())
      .then(data => setCourses(data));
  }, []);

  // Calculate stats for played courses - moved to top level to avoid hooks order issues
  const stats = useMemo(() => {
    if (!profile || !profile.playedCourses || !courses.length) {
      return {
        totalCourses: 0,
        states: new Set(),
        top1Courses: 0,
        top10Courses: 0,
        top25Courses: 0,
        top50Courses: 0,
        top100Courses: 0,
        stateWithMostCourses: null,
        mostCoursesInState: 0
      };
    }

    const playedCourses = profile.playedCourses || [];
    const courseInfo = playedCourses.map(id => courses.find(c => c.course_id === id)).filter(Boolean);
    const states = new Set(courseInfo.map(c => c.state).filter(Boolean));
    
    // Calculate courses per state
    const coursesPerState = {};
    courseInfo.forEach(course => {
      if (course.state) {
        coursesPerState[course.state] = (coursesPerState[course.state] || 0) + 1;
      }
    });
    
    // Find state with most courses
    let stateWithMostCourses = null;
    let mostCoursesInState = 0;
    Object.entries(coursesPerState).forEach(([state, count]) => {
      if (count > mostCoursesInState) {
        mostCoursesInState = count;
        stateWithMostCourses = state;
      }
    });
    
      const top1Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 1).length;
  const top10Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 10).length;
  const top25Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 25).length;
  const top50Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 50).length;
  const top100Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 100).length;
  
  // National ranking badge counts
  const top1NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 1).length;
  const top10NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 10).length;
  const top25NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 25).length;
  const top50NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 50).length;
  const top100NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 100).length;
  
  // Public ranking badge counts
  const top1PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 1).length;
  const top10PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 10).length;
  const top25PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 25).length;
  const top50PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 50).length;
  const top100PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 100).length;

    return {
      totalCourses: playedCourses.length,
      states,
      top1Courses,
      top10Courses,
      top25Courses,
      top50Courses,
      top100Courses,
      stateWithMostCourses,
      mostCoursesInState,
      top1NationalCourses,
      top10NationalCourses,
      top25NationalCourses,
      top50NationalCourses,
      top100NationalCourses,
      top1PublicCourses,
      top10PublicCourses,
      top25PublicCourses,
      top50PublicCourses,
      top100PublicCourses
    };
  }, [profile, courses]);

  const getCourseInfo = (courseId) => courses.find(c => c.course_id === courseId);



  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Profile - Golf Near Me</title>
          <meta name="description" content="Loading user profile..." />
        </Helmet>
        <div style={{ textAlign: "center", marginTop: 40 }}>Loading profile...</div>
      </>
    );
  }
  if (notFound) {
    return (
      <>
        <Helmet>
          <title>User Not Found - Golf Near Me</title>
          <meta name="description" content="The requested user profile could not be found." />
        </Helmet>
        <div style={{ textAlign: "center", marginTop: 40 }}>User not found or profile is private.</div>
      </>
    );
  }
  if (!profile) {
    return (
      <>
        <Helmet>
          <title>User Not Found - Golf Near Me</title>
          <meta name="description" content="The requested user profile could not be found." />
        </Helmet>
        <div style={{ textAlign: "center", marginTop: 40 }}>User not found or profile is private.</div>
      </>
    );
  }

  // If profile is private, only show username and a message
  if (profile.private) {
    return (
      <div style={{ 
        maxWidth: isMobile ? '100%' : 1200, 
        margin: isMobile ? "1rem 1rem 0 1rem" : "2rem auto 0 auto", 
        padding: isMobile ? '0' : '0 2rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '1.5rem' : '3rem',
        alignItems: 'flex-start',
        boxSizing: 'border-box'
      }}>
        <Helmet>
          <title>{profile.displayName || profile.username || 'User'}'s Profile - Golf Near Me</title>
          <meta name="description" content={`View ${profile.displayName || profile.username || 'this user'}'s golf course lists and profile.`} />
        </Helmet>
        {/* Profile Info Card */}
        <div style={{ 
          width: isMobile ? 'calc(100% - 2rem)' : 350,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          padding: isMobile ? '1.25rem' : '2rem'
        }}>
          {/* Header with Avatar and Username */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 16, 
            marginBottom: '2rem'
          }}>
            {/* Avatar */}
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 36,
              fontWeight: 700,
              color: '#b0b4ba',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>{profile.username && profile.username[0]}</div>
            
            {/* Username Badge */}
            <div style={{
              background: '#f3f4f6',
              borderRadius: 12,
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ 
                fontSize: 16, 
                fontWeight: 600, 
                color: '#374151'
              }}>
                {profile.username || 'Username'}
              </span>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', color: '#c00', fontSize: 18, fontWeight: 600 }}>
            This profile is private.
          </div>
        </div>
      </div>
    );
  }

  const avatarLetter = (profile.username && profile.username[0]) || '?';
  const playedCourses = profile.playedCourses || [];
  const wishlistCourses = profile.wishlistCourses || [];
  const customLists = profile.customLists || {};

  // Helper function to sort courses by rank
  const sortCoursesByRank = (courseIds) => {
    return [...courseIds].sort((a, b) => {
      const ca = getCourseInfo(a);
      const cb = getCourseInfo(b);
      
      // First priority: GD100all_rank (National ranking)
      const aNationalRank = ca && ca.GD100all_rank && ca.GD100all_rank !== '' ? parseInt(ca.GD100all_rank) : 9999;
      const bNationalRank = cb && cb.GD100all_rank && cb.GD100all_rank !== '' ? parseInt(cb.GD100all_rank) : 9999;
      if (aNationalRank !== bNationalRank) return aNationalRank - bNationalRank;
      
      // Second priority: GD100pub_rank (Public ranking)
      const aPublicRank = ca && ca.GD100pub_rank && ca.GD100pub_rank !== '' ? parseInt(ca.GD100pub_rank) : 9999;
      const bPublicRank = cb && cb.GD100pub_rank && cb.GD100pub_rank !== '' ? parseInt(cb.GD100pub_rank) : 9999;
      if (aPublicRank !== bPublicRank) return aPublicRank - bPublicRank;
      
      // Third priority: top100_state_rank (State ranking)
      const aStateRank = ca && ca.top100_state_rank > 0 ? ca.top100_state_rank : 9999;
      const bStateRank = cb && cb.top100_state_rank > 0 ? cb.top100_state_rank : 9999;
      return aStateRank - bStateRank;
    });
  };

  // Sort playedCourses: ranked first by rank, then unranked
  const sortedPlayedCourses = sortCoursesByRank(playedCourses);
  // Sort wishlistCourses: ranked first by rank, then unranked
  const sortedWishlistCourses = sortCoursesByRank(wishlistCourses);

  // Sort custom lists by course count (most courses first)
  const sortedCustomLists = Object.values(customLists).sort((a, b) => {
    return b.courses.length - a.courses.length;
  });

  return (
    <div style={{ 
      maxWidth: isMobile ? '100%' : 1200, 
      margin: isMobile ? "1rem 1rem 0 1rem" : "2rem auto 0 auto", 
      padding: isMobile ? '0' : '0 2rem',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      boxSizing: 'border-box',
      gap: isMobile ? '1.5rem' : '3rem',
      alignItems: 'flex-start'
    }}>
      <Helmet>
        <title>{profile.displayName || profile.username || 'User'}'s Profile - Golf Near Me</title>
        <meta name="description" content={`View ${profile.displayName || profile.username || 'this user'}'s golf course lists, played courses, and wishlist.`} />
      </Helmet>
      {/* Profile Info Card */}
      <div style={{ 
        width: isMobile ? 'calc(100% - 2rem)' : 350,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: isMobile ? '1.25rem' : '2rem'
      }}>
        {/* Header with Avatar and Username */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16, 
          marginBottom: '2rem'
        }}>
          {/* Avatar */}
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 36,
            fontWeight: 700,
            color: '#b0b4ba',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}>{avatarLetter}</div>
          
          {/* Username Badge */}
          <div style={{
            background: '#f3f4f6',
            borderRadius: 12,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ 
              fontSize: 16, 
              fontWeight: 600, 
              color: '#374151'
            }}>
              {profile.username || 'Username'}
            </span>
          </div>
        </div>

        {/* User Details */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Display Name</span>
            <div style={{ color: '#374151', fontSize: 16, fontWeight: 500, marginTop: '4px' }}>
              {profile.displayName || 'Not set'}
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Location</span>
            <div style={{ color: '#6b7280', fontSize: 16, fontWeight: 500, marginTop: '4px' }}>
              {profile.location || 'Not set'}
            </div>
          </div>
        </div>

        {/* Bio Section - Only show if user has typed something */}
        {profile.bio && profile.bio.trim() && (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 600 }}>
                About <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{profile.displayName || 'Display Name'}</span>
              </span>
            </div>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 15, 
              lineHeight: 1.6,
              fontStyle: 'italic'
            }}>
              {profile.bio}
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 600 }}>
              Course Stats
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.totalCourses} course{stats.totalCourses !== 1 ? 's' : ''} across {stats.states.size} state{stats.states.size !== 1 ? 's' : ''}
            </div>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              State with most courses played: {stats.stateWithMostCourses || 'N/A'} ({stats.mostCoursesInState || 0} course{(stats.mostCoursesInState || 0) !== 1 ? 's' : ''})
            </div>
          </div>
        </div>

        {/* National Ranking Badges Section */}
        {stats.top100NationalCourses > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.top100NationalCourses} of the top 100 courses in America
            </div>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
              marginTop: '8px',
              marginLeft: '-0.5rem'
            }}>
              {stats.top1NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top1all_badge.png" 
                    alt="Top 1 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top1NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top10NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top10all_badge.png" 
                    alt="Top 10 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top10NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top25NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top25all_badge.png" 
                    alt="Top 25 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top25NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top50NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top50all_badge.png" 
                    alt="Top 50 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top50NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top100NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top100all_badge.png" 
                    alt="Top 100 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top100NationalCourses}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Public Ranking Badges Section */}
        {stats.top100PublicCourses > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.top100PublicCourses} of the top 100 public courses in America
            </div>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
              marginTop: '8px',
              marginLeft: '-0.5rem'
            }}>
              {stats.top1PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top1pub_badge.png" 
                    alt="Top 1 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top1PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top10PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top10pub_badge.png" 
                    alt="Top 10 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top10PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top25PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top25pub_badge.png" 
                    alt="Top 25 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top25PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top50PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top50pub_badge.png" 
                    alt="Top 50 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top50PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top100PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top100pub_badge.png" 
                    alt="Top 100 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top100PublicCourses}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* State Ranking Badges Section */}
        {stats.top100Courses > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.top100Courses} in-state ranked course{stats.top100Courses !== 1 ? 's' : ''}
            </div>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
              marginTop: '8px',
              marginLeft: '-0.5rem'
            }}>
              {stats.top1Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top1state_badge.png" 
                    alt="Top 1 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top1Courses}
                  </span>
                </div>
              )}
              
              {stats.top10Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top10state_badge.png" 
                    alt="Top 10 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top10Courses}
                  </span>
                </div>
              )}
              
              {stats.top25Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top25state_badge.png" 
                    alt="Top 25 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top25Courses}
                  </span>
                </div>
              )}
              
              {stats.top50Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top50state_badge.png" 
                    alt="Top 50 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top50Courses}
                  </span>
                </div>
              )}
              
              {stats.top100Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top100state_badge.png" 
                    alt="Top 100 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top100Courses}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>



      {/* Lists Section */}
      <div style={{ 
        width: isMobile ? 'calc(100% - 2rem)' : 700,
        background: '#fff', 
        borderRadius: 16, 
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)', 
        padding: isMobile ? '1.25rem' : '1.5rem 1.5rem 1rem 1.5rem' 
      }}>
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #eee',
          marginBottom: '1.5rem',
          overflowX: 'auto'
        }}>
          <button
            onClick={() => setActiveTab('played')}
            style={{
              background: 'none',
              border: 'none',
              padding: '12px 20px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === 'played' ? '600' : '400',
              color: activeTab === 'played' ? '#1D665D' : '#666',
              borderBottom: activeTab === 'played' ? '2px solid #1D665D' : '2px solid transparent',
              whiteSpace: 'nowrap'
            }}
          >
            {profile.playedListName || 'Played'} ({sortedPlayedCourses.length})
          </button>
          <button
            onClick={() => setActiveTab('wishlist')}
            style={{
              background: 'none',
              border: 'none',
              padding: '12px 20px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === 'wishlist' ? '600' : '400',
              color: activeTab === 'wishlist' ? '#1D665D' : '#666',
              borderBottom: activeTab === 'wishlist' ? '2px solid #1D665D' : '2px solid transparent',
              whiteSpace: 'nowrap'
            }}
          >
            {profile.wishlistListName || 'Bucket List'} ({sortedWishlistCourses.length})
          </button>
          {sortedCustomLists.map(list => (
            <button
              key={list.id}
              onClick={() => setActiveTab(list.id)}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 20px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === list.id ? '600' : '400',
                color: activeTab === list.id ? '#1D665D' : '#666',
                borderBottom: activeTab === list.id ? '2px solid #1D665D' : '2px solid transparent',
                whiteSpace: 'nowrap'
              }}
            >
              {list.name} ({list.courses.length})
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'played' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1D665D', margin: 0 }}>{profile.playedListName || 'Played'}</h2>
                {sortedPlayedCourses.length > 0 && (
                  <button
                    onClick={() => {
                      // Create a dynamic live list URL that always shows current contents
                      window.location.href = `/map?list=live&type=played&user=${profile.uid}`;
                    }}
                    style={{
                      background: '#1D665D',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View on Map
                  </button>
                )}
              </div>
              

              {sortedPlayedCourses.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15, fontStyle: 'italic' }}>No courses marked as played yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortedPlayedCourses.map(cid => {
                    const c = getCourseInfo(cid);
                    return c ? (
                      <li key={cid} style={{ 
                        padding: '10px 0', 
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <span style={{ fontWeight: 600, color: '#222' }}>
                          <a href={`/course/${c.course_id}`} style={{ color: '#1D665D', textDecoration: 'underline' }}>{c.name}</a>
                        </span>
                        <span style={{ color: '#888', marginLeft: 8 }}>{c.club_name ? `at ${c.club_name} in ` : 'in '}{c.city}{c.city && c.state ? ', ' : ''}{c.state}</span>
                        <div style={{ fontSize: 14, color: '#1D665D', fontWeight: 500, margin: '2px 0 0 0' }}>
                          {c.GD100all_rank && c.GD100all_rank !== '' ? `#${c.GD100all_rank} in America` : null}
                          {c.GD100pub_rank && c.GD100pub_rank !== '' ? (c.GD100all_rank && c.GD100all_rank !== '' ? ` • #${c.GD100pub_rank} Public in America` : `#${c.GD100pub_rank} Public in America`) : null}
                          {c.top100_state_rank > 0 && c.state ? ((c.GD100all_rank && c.GD100all_rank !== '') || (c.GD100pub_rank && c.GD100pub_rank !== '') ? ` • #${c.top100_state_rank} in ${c.state}` : `#${c.top100_state_rank} in ${c.state}`) : null}
                        </div>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'wishlist' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1D665D', margin: 0 }}>{profile.wishlistListName || 'Bucket List'}</h2>
                {sortedWishlistCourses.length > 0 && (
                  <button
                    onClick={() => {
                      // Create a dynamic live list URL that always shows current contents
                      window.location.href = `/map?list=live&type=wishlist&user=${profile.uid}`;
                    }}
                    style={{
                      background: '#1D665D',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View on Map
                  </button>
                )}
              </div>
              

              {sortedWishlistCourses.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15, fontStyle: 'italic' }}>No courses in wishlist yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortedWishlistCourses.map(cid => {
                    const c = getCourseInfo(cid);
                    return c ? (
                      <li key={cid} style={{ 
                        padding: '10px 0', 
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <span style={{ fontWeight: 600, color: '#222' }}>
                          <a href={`/course/${c.course_id}`} style={{ color: '#1D665D', textDecoration: 'underline' }}>{c.name}</a>
                        </span>
                        <span style={{ color: '#888', marginLeft: 8 }}>{c.club_name ? `at ${c.club_name} in ` : 'in '}{c.city}{c.city && c.state ? ', ' : ''}{c.state}</span>
                        <div style={{ fontSize: 14, color: '#1D665D', fontWeight: 500, margin: '2px 0 0 0' }}>
                          {c.GD100all_rank && c.GD100all_rank !== '' ? `#${c.GD100all_rank} in America` : null}
                          {c.GD100pub_rank && c.GD100pub_rank !== '' ? (c.GD100all_rank && c.GD100all_rank !== '' ? ` • #${c.GD100pub_rank} Public in America` : `#${c.GD100pub_rank} Public in America`) : null}
                          {c.top100_state_rank > 0 && c.state ? ((c.GD100all_rank && c.GD100all_rank !== '') || (c.GD100pub_rank && c.GD100pub_rank !== '') ? ` • #${c.top100_state_rank} in ${c.state}` : `#${c.top100_state_rank} in ${c.state}`) : null}
                        </div>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </div>
          )}

          {activeTab !== 'played' && activeTab !== 'wishlist' && customLists[activeTab] && (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1D665D', margin: 0 }}>
                    {customLists[activeTab].name}
                  </h2>
                  {customLists[activeTab].courses.length > 0 && (
                    <button
                      onClick={() => {
                        // Create a dynamic live list URL that always shows current contents
                        window.location.href = `/map?list=live&type=custom&listId=${activeTab}&user=${profile.uid}`;
                      }}
                      style={{
                        background: '#1D665D',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      View on Map
                    </button>
                  )}
                </div>
              </div>
              

              {customLists[activeTab].courses.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15, fontStyle: 'italic' }}>
                  No courses in this list yet.
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortCoursesByRank(customLists[activeTab].courses).map(cid => {
                    const c = getCourseInfo(cid);
                    return c ? (
                      <li key={cid} style={{ 
                        padding: '10px 0', 
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <span style={{ fontWeight: 600, color: '#222' }}>
                          <a href={`/course/${c.course_id}`} style={{ color: '#1D665D', textDecoration: 'underline' }}>{c.name}</a>
                        </span>
                        <span style={{ color: '#888', marginLeft: 8 }}>{c.club_name ? `at ${c.club_name} in ` : 'in '}{c.city}{c.city && c.state ? ', ' : ''}{c.state}</span>
                        <div style={{ fontSize: 14, color: '#1D665D', fontWeight: 500, margin: '2px 0 0 0' }}>
                          {c.GD100all_rank && c.GD100all_rank !== '' ? `#${c.GD100all_rank} in America` : null}
                          {c.GD100pub_rank && c.GD100pub_rank !== '' ? (c.GD100all_rank && c.GD100all_rank !== '' ? ` • #${c.GD100pub_rank} Public in America` : `#${c.GD100pub_rank} Public in America`) : null}
                          {c.top100_state_rank > 0 && c.state ? ((c.GD100all_rank && c.GD100all_rank !== '') || (c.GD100pub_rank && c.GD100pub_rank !== '') ? ` • #${c.top100_state_rank} in ${c.state}` : `#${c.top100_state_rank} in ${c.state}`) : null}
                        </div>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicProfile; 